import {DetailedPrice} from '../services/priceInsightsApi'
import {
  HotelTrendDirection,
  MessageConfig,
  PriceTrendContent,
  PriceTrendInfoContent,
  PriceTrendInsightsPayload,
  SimilarHotelsDirection
} from '../types'
import {
  HOTEL_TREND_CONFIG,
  PRICE_TREND_CONFIG,
  SIMILAR_HOTELS_CONFIG
} from './constants'

/**
 * Classifies a price against the similar-hotels percentile distribution.
 *
 * Uses the same band rule as `PriceRangeBar.getIndicatorPercent`:
 * - hotelPrice ≤ p35  → 'low'   (Cheaper than similar)
 * - p35 < hotelPrice < p65 → 'fair' (Typical in area)
 * - hotelPrice ≥ p65  → 'high'  (Higher than similar)
 *
 * Returns `null` when the percentile data is missing or invalid; callers
 * should fall back to the median-based classifier in that case.
 */
export type SimilarHotelsBand = 'low' | 'fair' | 'high'

export const getSimilarHotelsBand = (
  currentPrice: number,
  detailedPrice: DetailedPrice | null | undefined
): SimilarHotelsBand | null => {
  if (
    !detailedPrice ||
    !Number.isFinite(currentPrice) ||
    !Number.isFinite(detailedPrice.p35) ||
    !Number.isFinite(detailedPrice.p65) ||
    detailedPrice.p35 > detailedPrice.p65
  ) {
    return null
  }

  if (currentPrice <= detailedPrice.p35) return 'low'
  if (currentPrice >= detailedPrice.p65) return 'high'
  return 'fair'
}

/**
 * Calculates the hotel change percentage by comparing current price to median hotel price.
 * @param currentPrice - The current offer price
 * @param medianHotelPrice - The median hotel price from the API
 * @returns Change percentage (negative = cheaper, positive = more expensive), or null if unavailable
 */
export const calculateHotelChangePercentage = (
  currentPrice: number,
  medianHotelPrice: number
): number | null => {
  if (
    medianHotelPrice === 0 ||
    !Number.isFinite(currentPrice) ||
    !Number.isFinite(medianHotelPrice)
  ) {
    return null
  }

  return (currentPrice - medianHotelPrice) / medianHotelPrice
}

/**
 * Calculates the market change percentage by comparing current price to similar hotels median price.
 * @param currentPrice - The current offer price
 * @param lastMedianSimilarHotelsPricePoint - The last median price of similar hotels from the API
 * @returns Change percentage (negative = cheaper, positive = more expensive), or null if unavailable
 */
export const calculateMarketChangePercentage = (
  currentPrice: number,
  lastMedianSimilarHotelsPricePoint: number | null
): number | null => {
  if (
    lastMedianSimilarHotelsPricePoint === null ||
    lastMedianSimilarHotelsPricePoint === 0 ||
    !Number.isFinite(currentPrice) ||
    !Number.isFinite(lastMedianSimilarHotelsPricePoint)
  ) {
    return null
  }

  return (
    (currentPrice - lastMedianSimilarHotelsPricePoint) /
    lastMedianSimilarHotelsPricePoint
  )
}

/**
 * Sanitizes a percentage value by clamping it to standard bounds (-100% to 100%)
 * and handling invalid values.
 * @param value - Percentage value to sanitize
 * @returns Sanitized percentage value between -1 and 1, or 0 for invalid values
 */
export const sanitizePercentage = (value: number | null): number => {
  if (value === null || !Number.isFinite(value)) {
    return 0
  }

  // Clamp to standard percentage bounds (-100% to 100%)
  return Math.max(-1, Math.min(1, value))
}

/**
 * Determines the hotel trend direction based on percentage change.
 * @param value - Percentage change value
 * @param threshold - Threshold for determining if change is meaningful (from API or fallback)
 * @returns Hotel trend direction: 'up', 'down', or 'stable'
 */
export const getHotelTrendDirection = (
  value: number,
  threshold: number = PRICE_TREND_CONFIG.thresholds.noChange
): HotelTrendDirection => {
  if (Math.abs(value) <= threshold) {
    return HOTEL_TREND_CONFIG.states.STABLE
  }

  return value > 0
    ? HOTEL_TREND_CONFIG.states.UP
    : HOTEL_TREND_CONFIG.states.DOWN
}

/**
 * Determines the similar hotels direction based on percentage difference.
 * @param value - Percentage difference value
 * @param threshold - Threshold for determining if difference is meaningful (from API or fallback)
 * @returns Similar hotels direction: 'expensive', 'cheaper', or 'same'
 */
export const getSimilarHotelsDirection = (
  value: number,
  threshold: number = PRICE_TREND_CONFIG.thresholds.noChange
): SimilarHotelsDirection => {
  if (Math.abs(value) <= threshold) {
    return SIMILAR_HOTELS_CONFIG.states.SAME
  }

  return value > 0
    ? SIMILAR_HOTELS_CONFIG.states.EXPENSIVE
    : SIMILAR_HOTELS_CONFIG.states.CHEAPER
}

/**
 * Creates the recommendation message based on percentile bucket (preferred)
 * or median-distance (fallback).
 *
 * Priority order:
 * 1. If `detailedPrice` is available, classify by which percentile band the
 *    current price sits in (matches the `PriceRangeBar` indicator). The
 *    percentage value used in copy ("X% cheaper / higher") is still derived
 *    from the median comparison, so the magnitude tracks `marketChangePercentage`.
 * 2. If percentiles unavailable but median is, fall back to the prior
 *    median ±threshold classifier.
 * 3. If both market signals unavailable, fall back to the hotel's own trend.
 *
 * @param hotelChangePercentage - How the hotel's price has changed over time (negative = cheaper now, positive = more expensive now)
 * @param marketChangePercentage - How the hotel compares with similar hotels (negative = cheaper, positive = more expensive, null = not available)
 * @param hotelChangePercentageThreshold - Threshold from API for determining if hotel change is meaningful
 * @param marketChangePercentageThreshold - Threshold from API for determining if market change is meaningful
 * @param detailedPrice - Latest similar-hotels percentile distribution (preferred classifier)
 * @param currentPrice - Hotel's current price, in the same unit as the percentiles
 * @returns MessageConfig with the appropriate recommendation message key
 */
export const createRecommendationMessage = (
  hotelChangePercentage: number,
  marketChangePercentage: number | null,
  hotelChangePercentageThreshold: number,
  marketChangePercentageThreshold: number,
  detailedPrice?: DetailedPrice | null,
  currentPrice?: number | null
): MessageConfig => {
  // Primary: percentile-bucket classifier. Keeps the bar and the banner
  // in sync — see the `getSimilarHotelsBand` docstring for the band rule.
  const band =
    currentPrice != null
      ? getSimilarHotelsBand(currentPrice, detailedPrice)
      : null

  const messageKey: MessageConfig['messageKey'] = (() => {
    if (band === 'low') return 'recommendationCheaperThanSimilar'
    if (band === 'high') return 'recommendationHigherThanSimilar'
    if (band === 'fair') return 'recommendationTypicalInArea'

    // Fallback: median ±threshold classifier (no percentiles)
    if (marketChangePercentage !== null) {
      if (marketChangePercentage < -marketChangePercentageThreshold) {
        return 'recommendationCheaperThanSimilar'
      }
      if (marketChangePercentage > marketChangePercentageThreshold) {
        return 'recommendationHigherThanSimilar'
      }
      return 'recommendationTypicalInArea'
    }

    // Last resort: hotel's own trend
    if (hotelChangePercentage < -hotelChangePercentageThreshold) {
      return 'recommendationPricesDropped'
    }
    if (hotelChangePercentage > hotelChangePercentageThreshold) {
      return 'recommendationPricesIncreased'
    }
    return 'recommendationPricesStable'
  })()

  return {messageKey}
}

const createHotelTitleMessage = (
  direction: HotelTrendDirection,
  percentage: number
): MessageConfig => {
  const messageKey = HOTEL_TREND_CONFIG.titleMessageByDirection[direction]

  // Stable direction doesn't need percentage value
  if (direction === HOTEL_TREND_CONFIG.states.STABLE) {
    return {messageKey}
  }

  const percent = Math.round(Math.abs(percentage) * 100)

  return {
    messageKey,
    values: {percent}
  }
}

const createHotelSubtitleMessage = (timeRangeDays: number): MessageConfig => ({
  ...HOTEL_TREND_CONFIG.subtitleMessage,
  values: {timeRange: timeRangeDays}
})

const createSimilarHotelsTitleMessage = (
  direction: SimilarHotelsDirection,
  percentage: number
): MessageConfig => {
  const messageKey = SIMILAR_HOTELS_CONFIG.titleMessageByDirection[direction]
  // Always emit `percent`. The banner reads this value to render "X% cheaper
  // / higher than similar hotels"; without it the banner falls back to the
  // generic "Fair price" copy. That fallback contradicts the percentile
  // classifier when the median direction is SAME but the bar pin is in low /
  // high (e.g. tight distribution where the hotel sits below p35 yet within
  // ±10% of the median). The info card's `similarHotelsNoDifference` message
  // has no `{percent}` placeholder, so an unused value is harmless there.
  const percent = Math.round(Math.abs(percentage) * 100)
  return {
    messageKey,
    values: {percent}
  }
}

const createHotelInfoContent = (
  payload: PriceTrendInsightsPayload,
  changePercentage: number,
  direction: HotelTrendDirection
): PriceTrendInfoContent => ({
  title: createHotelTitleMessage(direction, changePercentage),
  subtitle: createHotelSubtitleMessage(payload.timeRangeDays),
  tooltip: {
    ...HOTEL_TREND_CONFIG.tooltipMessage,
    values: {lastDays: payload.timeRangeDays}
  },
  icon: HOTEL_TREND_CONFIG.iconByDirection[direction]
})

const createSimilarHotelsInfoContent = (
  marketChangePercentage: number,
  direction: SimilarHotelsDirection
): PriceTrendInfoContent => ({
  title: createSimilarHotelsTitleMessage(direction, marketChangePercentage),
  subtitle: SIMILAR_HOTELS_CONFIG.subtitleMessage,
  tooltip: SIMILAR_HOTELS_CONFIG.tooltipMessage,
  icon: SIMILAR_HOTELS_CONFIG.iconByDirection[direction]
})

/**
 * Builds the complete price trend content including recommendation and info cards.
 * Calculates both hotel and market change percentages from the current price.
 *
 * @param payload - Price trend insights from the API
 * @param currentHotelPrice - The current hotel price to compare against
 * @param detailedPrice - Latest similar-hotels percentile distribution; when provided,
 *   takes precedence over the median classifier so the recommendation copy matches
 *   the band the `PriceRangeBar` indicator sits in.
 * @returns Complete price trend content for display
 */
export const buildPriceTrendContent = (
  payload: PriceTrendInsightsPayload,
  currentHotelPrice: number,
  detailedPrice?: DetailedPrice | null
): PriceTrendContent => {
  // Calculate both percentages inside the function
  const hotelChangePercentage = calculateHotelChangePercentage(
    currentHotelPrice,
    payload.medianHotelPrice
  )
  const marketChangePercentage = calculateMarketChangePercentage(
    currentHotelPrice,
    payload.lastMedianSimilarHotelsPricePoint
  )

  // Sanitize percentages for display
  const sanitizedHotelChangePercentage = sanitizePercentage(
    hotelChangePercentage
  )
  const sanitizedMarketChangePercentage =
    marketChangePercentage !== null
      ? sanitizePercentage(marketChangePercentage)
      : null

  // Get directions using thresholds from API
  const hotelDirection = getHotelTrendDirection(
    sanitizedHotelChangePercentage,
    payload.hotelChangePercentageThreshold
  )

  return {
    isPriceDropped: sanitizedHotelChangePercentage < 0,
    recommendation: createRecommendationMessage(
      sanitizedHotelChangePercentage,
      sanitizedMarketChangePercentage,
      payload.hotelChangePercentageThreshold,
      payload.marketChangePercentageThreshold,
      detailedPrice,
      currentHotelPrice
    ),
    hotelChange: createHotelInfoContent(
      payload,
      sanitizedHotelChangePercentage,
      hotelDirection
    ),
    // Only include similarHotels content if market change percentage is available
    ...(sanitizedMarketChangePercentage !== null && {
      similarHotels: createSimilarHotelsInfoContent(
        sanitizedMarketChangePercentage,
        getSimilarHotelsDirection(
          sanitizedMarketChangePercentage,
          payload.marketChangePercentageThreshold
        )
      )
    })
  }
}
